import React from 'react';
import { useTheme } from '@veneer/theme';
import { SerializedStyles, css } from '@emotion/react';

const useStyles = (): {
  root: {
    base: SerializedStyles;
    minimalCentered: SerializedStyles;
    notMinimalCentered: {
      base: SerializedStyles;
      tiered: SerializedStyles;
    };
    hasSecondary: SerializedStyles;
    ultraMinimal: SerializedStyles;
    hc: SerializedStyles;
  };
  rootSecondary: {
    base: SerializedStyles;
    hc: SerializedStyles;
  };
  links: {
    base: SerializedStyles;
    minimalCentered: SerializedStyles;
  };
  linkPrimary: {
    base: SerializedStyles;
    hc: SerializedStyles;
  };
  leadingArea: {
    base: SerializedStyles;
    minimalCentered: SerializedStyles;
  };
  trailingArea: {
    base: SerializedStyles;
    minimalCentered: SerializedStyles;
  };
} => {
  const theme = useTheme();

  return React.useMemo(() => {
    return {
      root: {
        base: css({
          backgroundColor: theme.color.background.container.default,
          borderColor: theme.color.background.container.default,
          borderStyle: 'solid',
          borderWidth: '1px',
          boxSizing: 'border-box',
          display: 'flex',
          padding: `calc(${theme.orbitalEight} - 1px)`,
          width: '100%',
        }),
        minimalCentered: css({
          flexDirection: 'column-reverse',
        }),
        notMinimalCentered: {
          base: css({
            alignItems: 'center',
          }),
          tiered: css({
            alignItems: 'flex-start',
          }),
        },
        hasSecondary: css({
          borderBottom: `calc(${theme.layout.size2} + 2px) solid ${theme.color.background.container.default}`,
        }),
        ultraMinimal: css({
          padding: `calc(${theme.orbitalFive} - 1px)`,
        }),
        hc: css({
          borderColor: theme.color.foreground.default,
        }),
      },
      rootSecondary: {
        base: css({
          borderBottom: `1px solid ${theme.color.background.container.default}`,
          position: 'relative',

          '::before': {
            alignSelf: 'flex-start',
            backgroundColor: theme.color.stroke.decorative.default,
            content: "' '",
            height: '1px',
            left: '30px',
            marginTop: `calc(-${theme.orbitalEight} - 1px)`,
            position: 'absolute',
            width: 'calc(100% - 2 * 30px)',
          },
        }),

        hc: css({
          borderTop: `1px solid ${theme.color.background.container.default}`,
        }),
      },
      links: {
        base: css({
          display: 'flex',
          flexGrow: 1,
          '> ul': {
            display: 'flex',
            flexGrow: 1,
            flexShrink: 0,
            justifyContent: 'space-evenly',
            '> li': {
              display: 'block',
              paddingLeft: '8px',
              paddingRight: '8px',
              '> ul': {
                paddingTop: '16px',
              },
            },
          },

          '>* .vn-footer__link': {
            ...theme.typography.captionRegular,
            // Overriding the textDecoration from CssBaseline, as indicated by the exception on # 6895's conclusion
            textDecoration: 'none',
            color: theme.color.primary.base.default,
            ':hover': {
              color: theme.color.primary.base.hover,
              textDecoration: 'underline',
            },
            ':active': {
              color: theme.color.primary.base.active,
              textDecoration: 'underline',
            },
          },
        }),
        minimalCentered: css({
          paddingTop: '16px',
        }),
      },
      linkPrimary: {
        base: css({
          '> ul': {
            '> li > li:first-of-type > .vn-footer__link': {
              color: theme.color.foreground.default,
              ...theme.typography.labelRegular,
            },

            '> li > .vn-footer__link': {
              color: theme.color.foreground.default,
              ...theme.typography.labelRegular,
            },
          },
        }),
        hc: css({
          '> ul': {
            '> li > li:first-of-type > .vn-footer__link': {
              color: theme.color.neutral.base.default,
            },
            '> li > .vn-footer__link': {
              color: theme.color.neutral.base.default,
            },
          },
        }),
      },
      leadingArea: {
        base: css({
          color: theme.color.foreground.default,
          display: 'flex',
          flexGrow: 1,
          flexShrink: 0,
          ...theme.typography.subtitleRegular,
        }),

        minimalCentered: css({
          justifyContent: 'center',
          paddingTop: '50px',
        }),
      },
      trailingArea: {
        base: css({
          color: theme.color.foreground.default,
          display: 'flex',
          flexGrow: 1,
          flexShrink: 1,
          justifyContent: 'flex-end',
          ...theme.typography.subtitleRegular,
        }),

        minimalCentered: css({
          justifyContent: 'center',
        }),
      },
    };
  }, [theme]);
};

export default useStyles;
